import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { apiClient } from "../../api/client";
import type { PlaceMapItem, PlaceMapResponse } from "../../api/types";
import { AddressSearch } from "../../components/AddressSearch";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { openDirectionsWithFallback } from "../directions/providers";
import { usePreferredDirectionOrder } from "../directions/useDirections";
import { categoryMatchesSelection, PLACE_CATEGORY_OPTIONS } from "../../lib/placeCategories";

// Fix default icon paths broken by bundlers
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const CIN_LAT = 39.1031;
const CIN_LNG = -84.512;
// Fetch a wide area — viewport handles what's actually shown
const FETCH_RADIUS_KM = 80;

async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=14`,
      { headers: { "Accept-Language": "en", "User-Agent": "nutoverdose-app/0.1" } },
    );
    const data = (await res.json()) as { address?: { neighbourhood?: string; suburb?: string; city?: string; town?: string } };
    const a = data.address ?? {};
    return a.neighbourhood ?? a.suburb ?? a.city ?? a.town ?? "Your Location";
  } catch {
    return "Your Location";
  }
}

const STATUS_COLORS: Record<string, string> = {
  open: "#0a9f67",
  likely: "#ca8617",
  unknown: "#6a7384",
  closed: "#ce3047",
};

const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  likely: "Likely Open",
  unknown: "Unknown",
  closed: "Closed",
};

const TILE_LAYER_OPTIONS = {
  attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
  subdomains: "abcd",
  maxZoom: 20,
} as const;

function getTileLayerUrl(theme: "light" | "dark"): string {
  if (theme === "dark") {
    return "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
  }
  return "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
}

function makeCircleMarker(item: PlaceMapItem): L.CircleMarker {
  const color = STATUS_COLORS[item.status] ?? STATUS_COLORS.unknown;
  return L.circleMarker([item.lat, item.lng], {
    radius: 10,
    fillColor: color,
    fillOpacity: 0.88,
    color: "#ffffff",
    weight: 2,
  });
}

function makeUserPin(lat: number, lng: number): L.Marker {
  const icon = L.divIcon({
    className: "",
    html: `<div style="
      width:18px;height:18px;
      background:#ffad3b;
      border:3px solid #fff;
      border-radius:50%;
      box-shadow:0 0 6px rgba(0,0,0,.5);
    "></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
  return L.marker([lat, lng], { icon, zIndexOffset: 1000 });
}

export function MapPage({ onSignInPrompt }: { onSignInPrompt?: () => void }) {
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const mapRef = useRef<L.Map | null>(null);
  const mapDivRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.CircleMarker[]>([]);
  const userPinRef = useRef<L.Marker | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);

  const [location, setLocation] = useState({ lat: CIN_LAT, lng: CIN_LNG });
  const [locLabel, setLocLabel] = useState("Cincinnati");
  const [category, setCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [showCustomCategoryInput, setShowCustomCategoryInput] = useState(false);
  const [allItems, setAllItems] = useState<PlaceMapItem[]>([]);
  const [selected, setSelected] = useState<PlaceMapItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmDone, setConfirmDone] = useState<string | null>(null);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [inViewCount, setInViewCount] = useState(0);
  const [inViewOpenOrLikelyCount, setInViewOpenOrLikelyCount] = useState(0);
  const directionOrder = usePreferredDirectionOrder("auto");

  // Filtered items based on category selection
  const customCategoryLower = customCategory.trim().toLowerCase();
  const visibleItems = allItems.filter((item) => {
    if (showCustomCategoryInput) {
      if (!customCategoryLower) return true;
      const categoryMatch = item.category.toLowerCase().includes(customCategoryLower);
      const nameMatch = item.name.toLowerCase().includes(customCategoryLower);
      return categoryMatch || nameMatch;
    }
    if (category) return categoryMatchesSelection(item.category, category);
    return true;
  });

  const availableCategoryOptions = useMemo(() => {
    return PLACE_CATEGORY_OPTIONS.filter((option) => {
      if (option.key === "" || option.key === "__search__") return true;
      if (option.key === category) return true;
      return allItems.some((item) => categoryMatchesSelection(item.category, option.key));
    });
  }, [allItems, category]);

  useEffect(() => {
    if (!category) return;
    const hasMatches = allItems.some((item) => categoryMatchesSelection(item.category, category));
    if (!hasMatches) {
      setCategory("");
    }
  }, [allItems, category]);

  // Geolocation on mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const label = await reverseGeocode(latitude, longitude);
        setLocation({ lat: latitude, lng: longitude });
        setLocLabel(label);
      },
      () => { /* stay on Cincinnati default */ },
      { timeout: 8000 },
    );
  }, []);

  // ── Init map once ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;

    const map = L.map(mapDivRef.current, {
      center: [CIN_LAT, CIN_LNG],
      zoom: 12,
      zoomControl: true,
    });

    mapRef.current = map;

    return () => {
      tileLayerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    tileLayerRef.current?.remove();
    const tileLayer = L.tileLayer(getTileLayerUrl(resolvedTheme), TILE_LAYER_OPTIONS).addTo(map);
    tileLayerRef.current = tileLayer;

    return () => {
      if (tileLayerRef.current === tileLayer) {
        tileLayer.remove();
        tileLayerRef.current = null;
      }
    };
  }, [resolvedTheme]);

  // ── Fetch all places once (wide radius, viewport is the filter) ───────────
  const fetchPlaces = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        lat: String(location.lat),
        lng: String(location.lng),
        radius_km: String(FETCH_RADIUS_KM),
      });
      const res = await apiClient.get<PlaceMapResponse>(`/map/places?${params}`);
      setAllItems(res.items);
    } catch {
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  }, [location.lat, location.lng]);

  useEffect(() => {
    void fetchPlaces();
  }, [fetchPlaces]);

  const updateInViewCounts = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      const openOrLikely = visibleItems.filter((item) => item.status === "open" || item.status === "likely").length;
      setInViewCount(visibleItems.length);
      setInViewOpenOrLikelyCount(openOrLikely);
      return;
    }

    const bounds = map.getBounds();
    const inViewItems = visibleItems.filter((item) => bounds.contains(L.latLng(item.lat, item.lng)));
    const openOrLikely = inViewItems.filter((item) => item.status === "open" || item.status === "likely").length;
    setInViewCount(inViewItems.length);
    setInViewOpenOrLikelyCount(openOrLikely);
  }, [visibleItems]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const syncCounts = () => updateInViewCounts();
    map.on("moveend zoomend resize", syncCounts);
    syncCounts();

    return () => {
      map.off("moveend zoomend resize", syncCounts);
    };
  }, [updateInViewCounts]);

  // ── Sync visible markers to map ───────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    visibleItems.forEach((item) => {
      const marker = makeCircleMarker(item).addTo(map);
      marker.on("click", () => {
        setSelected(item);
        setConfirmDone(null);
      });
      markersRef.current.push(marker);
    });
  }, [visibleItems]);

  // ── Pan map + update user pin when location changes ───────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.setView([location.lat, location.lng], map.getZoom());

    if (userPinRef.current) {
      userPinRef.current.remove();
      userPinRef.current = null;
    }

    const isDefault =
      Math.abs(location.lat - CIN_LAT) < 0.0001 &&
      Math.abs(location.lng - CIN_LNG) < 0.0001 &&
      locLabel === "Cincinnati";
    if (!isDefault) {
      const pin = makeUserPin(location.lat, location.lng).addTo(map);
      pin.bindPopup(`<b>📍 ${locLabel}</b>`);
      pin.bindTooltip("You are here", {
        permanent: true,
        direction: "bottom",
        offset: [0, 14],
        className: "you-here-tooltip",
      });
      userPinRef.current = pin;
    }
  }, [location, locLabel]);

  const handleAddressSelect = (lat: number, lng: number, label: string) => {
    setLocation({ lat, lng });
    setLocLabel(label);
    setSelected(null);
  };

  const handleCategoryClick = (key: string) => {
    setSelected(null);
    if (key === "__search__") {
      setShowCustomCategoryInput(true);
      setCategory("");
      return;
    }
    setShowCustomCategoryInput(false);
    setCustomCategory("");
    setCategory(key);
  };

  const confirm = async (placeId: string, status: "open" | "closed" | "not_sure") => {
    if (!user) {
      onSignInPrompt?.();
      return;
    }
    setConfirming(true);
    try {
      await apiClient.post(`/places/${placeId}/confirm`, { status });
      setConfirmDone(placeId);
      setTimeout(() => void fetchPlaces(), 800);
    } finally {
      setConfirming(false);
    }
  };

  const openDirections = () => {
    if (!selected) return;
    openDirectionsWithFallback(directionOrder[0] ?? "google", selected.lat, selected.lng);
  };

  const activeCategoryChip = showCustomCategoryInput ? "__search__" : category;

  return (
    <main className="map-page">
      <header className="map-topbar">
        <div className="map-title-row">
          <div>
            <h1>Open Late</h1>
            <p>
              {loading ? "Loading nearby places..." : `${inViewCount} place${inViewCount === 1 ? "" : "s"} in view`}
            </p>
          </div>
          <button
            type="button"
            className="loc-pill"
            onClick={() => setShowMobileSearch((v) => !v)}
            aria-label="Change location"
          >
            📍 {locLabel}
          </button>
        </div>
        <section className="map-glance" aria-label="Map summary">
          <article className="metric-card">
            <strong>{inViewCount}</strong>
            <span>In View</span>
          </article>
          <article className="metric-card">
            <strong>{inViewOpenOrLikelyCount}</strong>
            <span>Open Or Likely</span>
          </article>
        </section>
        <div className={showMobileSearch ? "address-search--open map-search-shell" : "map-search-shell"}>
          <AddressSearch onSelect={handleAddressSelect} currentLabel={locLabel} />
        </div>
      </header>

      <section className="map-canvas-shell">
        <div ref={mapDivRef} className="map-canvas" />

        <div className="map-legend">
          {Object.entries(STATUS_COLORS).map(([status, color]) => (
            <span key={status} className="map-legend-item">
              <span className="map-legend-dot" style={{ background: color }} />
              {STATUS_LABELS[status]}
            </span>
          ))}
        </div>

        {selected && (
          <div className="map-selected-card">
            <div className="map-selected-head">
              <div>
                <h3>{selected.name}</h3>
                <div className="map-selected-meta">
                  <span className="category-chip">{selected.category}</span>
                  <span className={`status-badge ${selected.status}`}>{STATUS_LABELS[selected.status]}</span>
                </div>
              </div>
              <button type="button" className="map-close-btn" onClick={() => setSelected(null)} aria-label="Close selected place">
                ✕
              </button>
            </div>

            {confirmDone === selected.place_id ? (
              <span className="map-confirmed-text">Signal recorded. Thanks.</span>
            ) : user ? (
              <div className="map-selected-actions">
                <button type="button" className="btn-open" disabled={confirming}
                  onClick={() => void confirm(selected.place_id, "open")}>
                  ✅ Open
                </button>
                <button type="button" className="btn-closed" disabled={confirming}
                  onClick={() => void confirm(selected.place_id, "closed")}>
                  🔴 Closed
                </button>
                <button type="button" className="btn-unsure" disabled={confirming}
                  onClick={() => void confirm(selected.place_id, "not_sure")}>
                  Not Sure
                </button>
              </div>
            ) : (
              <div className="map-selected-actions">
                <button type="button" className="btn-primary" onClick={() => onSignInPrompt?.()}>
                  Sign in to verify
                </button>
                <span className="map-signin-hint">Login unlocks open/closed confirmations.</span>
              </div>
            )}
          </div>
        )}
      </section>

      <footer className="map-bottom-cta">
        <div className="map-chip-row">
          {availableCategoryOptions.map((cat) => (
            <button
              key={cat.key}
              type="button"
              className={`filter-chip map-chip${activeCategoryChip === cat.key ? " active" : ""}`}
              onClick={() => handleCategoryClick(cat.key)}
            >
              {cat.label}
            </button>
          ))}
        </div>
        {showCustomCategoryInput && (
          <div className="map-custom-filter">
            <input
              type="text"
              className="custom-category-input"
              placeholder="Filter map by name or category..."
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
            />
          </div>
        )}
        <button type="button" className="map-directions-btn" onClick={openDirections} disabled={!selected}>
          {selected ? `Get Directions to ${selected.name}` : "Get Directions"}
        </button>
      </footer>
    </main>
  );
}
